import { AccountBalanceDataCard } from "@/components/common/section-components/card/AccountBalanceDataCard";
import { AccountMainDataCard } from "@/components/common/section-components/card/AccountMainDataCard";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TabComponent } from "@/components/tabs/TabComponent";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Flex, Grid, GridItem } from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AccountPersonData } from "../tabs/AccountPersonData";
import { AccountCBUData } from "../tabs/AccountCBUData";
import { AccountMobileUserData } from "../tabs/AccountMobileUserData";
import { AccountCVUData } from "../tabs/AccountCVUData";
import { BreadcrumbComponent } from "@/components/breadcrumb/Breadcrumb";
import { AccountData } from "../schema/account.schema";
import { useEffect, useState } from "react";
import { getAccountData } from "@/services/accounts.service";
import { CreditAgreementData } from "../tabs/CreditAgreementData";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { useRoles } from "@/hooks/useRoles";

export const AccountDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingAccountData, setLoadingAccountData] = useState<boolean>(false);
  const [accountData, setAccountData] = useState<AccountData | null>();
  const navigate = useNavigate();

  const location = useLocation();
  const handleBack = () => {
    //Chequear si este if funciona con todos los casos después
    if (location.state?.from) {
      return navigate(location.state.from);
    }
    navigate(AppRoutes.ACCOUNTS, {
      state: {
        page: location.state?.page ?? 1,
      },
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();

  useEffect(() => {
    if (isActive && id) {
      getAccountDetail(Number(id));
    }
  }, [isActive, id]);

  const { fetchPermissions } = useRoles();

  
  const getAccountDetail = async (id: number) => {
    try {
    setLoadingAccountData(true);
    const response = await getAccountData({
      NroCuenta: id,
    });

    setAccountData(response);

    setLoadingAccountData(false);
    } catch (error) {
      const errorObj = error as AxiosError<{ message: string }>;
      const handled = await handlePermissionError(
        error,
        navigate,
        fetchPermissions,
      );
      if (!handled) {
        toaster.create({
          type: "error",
          title:
            errorObj?.message ?? "No se pudieron obtener los datos de la cuenta.",
        });
      }
      throw error;
    }
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="10px">
      <SectionHeader
        loading={loadingAccountData}
        showButtons={false}
        title="Maestro de Cuentas"
        backButton
        onBack={handleBack}
        breadcrumbComponent={
          accountData && (
            <BreadcrumbComponent
              options={[
                {
                  label: "Maestro de cuentas",
                  href: AppRoutes.ACCOUNTS,
                },
                {
                  label: `Cuenta N° ${accountData?.cuenta}`,
                  href: "#",
                  strong: true,
                },
              ]}
            />
          )
        }
      />
      {accountData && (
        <>
          <Grid templateColumns="repeat(4, 1fr)" gap="4">
            <GridItem
              colSpan={3}
              padding="12px"
              borderRadius="12px"
              border={`1px solid ${AppColors.WHITE_BORDER_1}`}
            >
              <AccountMainDataCard accountData={accountData} />
              {/* Datos de cuenta corriente */}
            </GridItem>
            <GridItem colSpan={1} height="fit-content">
              {/* Datos de saldos */}
              <AccountBalanceDataCard accountData={accountData} />
            </GridItem>
          </Grid>

          <TabComponent
            tabs={[
              {
                label: "Personas",
                value: "personas",
                content: (
                  <AccountPersonData
                    accountPersonData={accountData?.personas}
                  />
                ),
              },
              {
                label: "CBU",
                value: "CBU",
                content: <AccountCBUData accountCbus={accountData?.cbus} />,
              },
              {
                label: "Usuarios mobile",
                value: "Usuarios mobile",
                content: (
                  <AccountMobileUserData
                    accountMobileUsers={accountData?.usuarios}
                  />
                ),
              },
              {
                label: "Acuerdo crédito",
                value: "Acuerdo crédito",
                content: (
                  <CreditAgreementData
                    creditAgreement={accountData?.acuerdoCredito}
                  />
                ),
              },
              {
                label: "CVU",
                value: "CVU",
                content: <AccountCVUData accountCvu={accountData?.cvu} />,
              },
            ]}
          />
        </>
      )}
    </Flex>
  );
};
